import { createStowagePlanChange } from 'src/factory/Stowage/StowagePlanChanges/StowagePlanChangeFactory';
import { StowageChangeDataType } from 'src/types/ViewModel/Stowage/Enums/StowageChangeDataType';
import { RotationCargo } from 'src/types/ViewModel/Stowage/Rotation/RotationCargo';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';
import { StowagePlanChange } from 'src/types/ViewModel/Stowage/StowagePlanChange';
import { getChangeDataGuid } from './stowagePlanChangeUtils/stowageChangeDataTypeUtils';
import { UUID } from 'crypto';

const getAllCargoOperations = (rotation: RotationPortCall[]): RotationCargoOperation[] =>
    rotation.flatMap((pc) => pc.berthCalls.flatMap((bc) => bc.cargoOperations));

/**
 * Returns true if the cargo-level `isContractWeightEdited` flag can safely be cleared —
 * i.e., no other loading operation for the same cargo (other than `currentOpGuid`) still
 * has an active, non-converging contract weight override.
 *
 * When `incomingCargoOpsById` is provided, a sibling loading op that has
 * `isContractWeightEdited: true` but whose incoming IMOS weight already matches its
 * override weight (within 0.001 tolerance) is treated as converging and therefore does
 * NOT block the cargo-level clear.
 */
export const shouldClearCargoContractWeightFlag = (
    allLoadingOps: RotationCargoOperation[],
    currentOpGuid: UUID,
    incomingCargoOpsById?: ReadonlyMap<number, RotationCargoOperation>
): boolean => {
    return !allLoadingOps.some((op) => {
        if (op.cargoOperationGuid === currentOpGuid) return false;
        if (op.isContractWeightEdited !== true) return false;
        // If incoming data is available, check whether this sibling would also converge.
        // A sibling whose incoming IMOS weight matches its current override is itself
        // about to be cleared — it must not block the cargo-level flag clear.
        if (incomingCargoOpsById != null && op.imosCargoOperationId != null) {
            const incoming = incomingCargoOpsById.get(op.imosCargoOperationId);
            if (
                incoming != null &&
                incoming.contractWeight != null &&
                op.contractWeight != null &&
                Math.abs(incoming.contractWeight - op.contractWeight) < 0.001
            ) {
                return false;
            }
        }
        return true;
    });
};

/**
 * Filters a list of unloading operations to those that have at least one valid (non-null)
 * snapshot field and should therefore be reverted when a contract weight override is cleared.
 */
export const filterRevertableUnloadingOps = (unloadingOps: RotationCargoOperation[]): RotationCargoOperation[] =>
    unloadingOps.filter((op) => op.originalContractWeightFromIMOS != null || op.originalNominatedWeightFromIMOS != null);

/**
 * Builds the cascade `StowagePlanChange` array needed when a loading operation's contract
 * weight override converges with the IMOS value during an IMOS sync comparison.
 *
 * Returns:
 * - A `RotationCargo` change clearing `isContractWeightEdited: false` — only when no other
 *   loading operation for the same cargo still has an active override.
 * - A `RotationCargoOperation` change per qualifying unloading operation that reverts
 *   `contractWeight` / `nominatedWeight` to their stored original IMOS snapshot values and
 *   nulls out those snapshot fields.
 *
 * Unloading operations are skipped when:
 * - Their IMOS ID is absent from `incomingCargoOpImosIds` (deleted in the incoming data).
 * - Both snapshot fields are null/undefined (no valid revert target).
 *
 * All returned changes carry `extraData.hideFromChangeLog: true` so they do not inflate the
 * sync-change count shown in the changelog UI.
 */
export const buildContractWeightClearCascadeChanges = (
    stowagePlanGuid: UUID,
    convergedLoadingOp: RotationCargoOperation,
    existingCargoes: RotationCargo[],
    existingRotation: RotationPortCall[],
    incomingCargoOpsById: ReadonlyMap<number, RotationCargoOperation>
): StowagePlanChange[] => {
    const cargo = convergedLoadingOp.cargo;
    if (!cargo) return [];

    const changes: StowagePlanChange[] = [];
    const allExistingOps = getAllCargoOperations(existingRotation);

    // Cargo-level clear: only emit if no other loading op for this cargo still has an override.
    const allLoadingOps = allExistingOps.filter((op) => op.operationType === 'Loading' && op.cargoGuid === cargo.cargoGuid);
    if (shouldClearCargoContractWeightFlag(allLoadingOps, convergedLoadingOp.cargoOperationGuid, incomingCargoOpsById)) {
        const existingCargo = existingCargoes.find((c) => c.cargoGuid === cargo.cargoGuid);
        if (existingCargo) {
            const guid = getChangeDataGuid(StowageChangeDataType.RotationCargo, existingCargo);
            changes.push(
                createStowagePlanChange(
                    stowagePlanGuid,
                    guid,
                    StowageChangeDataType.RotationCargo,
                    {
                        isContractWeightEdited: false,
                        extraData: { hideFromChangeLog: true },
                    },
                    true
                )
            );
        }
    }

    // Unloading revert: only for ops that are present in incoming and have valid snapshots.
    const eligibleUnloadingOps = allExistingOps.filter(
        (op) =>
            op.operationType === 'Unloading' &&
            op.cargoGuid === cargo.cargoGuid &&
            (op.imosCargoOperationId == null || incomingCargoOpsById.has(op.imosCargoOperationId))
    );
    for (const unloadingOp of filterRevertableUnloadingOps(eligibleUnloadingOps)) {
        const revertData: Record<string, unknown> = {
            extraData: { hideFromChangeLog: true },
        };
        if (unloadingOp.originalContractWeightFromIMOS != null) {
            revertData.contractWeight = unloadingOp.originalContractWeightFromIMOS;
            revertData.originalContractWeightFromIMOS = null;
        }
        if (unloadingOp.originalNominatedWeightFromIMOS != null) {
            revertData.nominatedWeight = unloadingOp.originalNominatedWeightFromIMOS;
            revertData.originalNominatedWeightFromIMOS = null;
        }
        const guid = getChangeDataGuid(StowageChangeDataType.RotationCargoOperation, unloadingOp);
        changes.push(createStowagePlanChange(stowagePlanGuid, guid, StowageChangeDataType.RotationCargoOperation, revertData, true));
    }

    return changes;
};
